import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";

import { cn } from "./cn";

/**
 * Card and Badge (shadcn/ui style).
 *
 * The card is a composition of small parts rather than one component with a
 * dozen props: a dashboard tile and a settings panel share a surface, not a
 * shape, and forcing them into one API produces a component nobody can extend.
 */

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("rounded-lg border border-neutral-200 bg-white shadow-sm", className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col gap-1 border-b border-neutral-100 p-4", className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("text-base font-semibold text-neutral-900", className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm text-neutral-500", className)} {...props} />;
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-4", className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex items-center gap-2 border-t border-neutral-100 p-4", className)}
      {...props}
    />
  );
}

/** A single number with a caption — the unit a dashboard is made of. */
export interface StatProps {
  readonly label: string;
  readonly value: number | string;
  readonly hint?: string;
}

export function Stat({ label, value, hint }: StatProps) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <p className="text-sm text-neutral-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-neutral-900">{value}</p>
      {hint === undefined ? null : <p className="mt-1 text-xs text-neutral-400">{hint}</p>}
    </div>
  );
}

export const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
  {
    variants: {
      variant: {
        default: "border-transparent bg-neutral-900 text-white",
        secondary: "border-transparent bg-neutral-100 text-neutral-700",
        outline: "border-neutral-300 text-neutral-600",
        success: "border-transparent bg-green-100 text-green-800",
        warning: "border-transparent bg-amber-100 text-amber-800",
        destructive: "border-transparent bg-red-100 text-red-800",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/**
 * Map a content status to a badge variant.
 *
 * Here rather than in each page so that "published is green" is one decision.
 * A status the map does not know falls back to `secondary` instead of throwing
 * — a new status added on the server should render as unfamiliar, not crash the
 * list.
 */
export function statusVariant(
  status: string,
): NonNullable<VariantProps<typeof badgeVariants>["variant"]> {
  switch (status) {
    case "published":
      return "success";
    case "scheduled":
      return "warning";
    case "draft":
      return "secondary";
    case "archived":
      return "outline";
    default:
      return "secondary";
  }
}
